import axios from 'axios';

import {
  createClientOperationId,
  EventJobStartResponseDTO,
  waitForEventJob,
} from '@/api/events/api';
import { createApiClient } from '@/api/http-client';
import { handleSessionExpired } from '@/api/session-expiry';
import { config } from '@/config';
import { createLogger } from '@/utils/logging';

const logger = createLogger('LlmConnectionsAPI', 'api');

/** Typed error thrown by LLM connection API functions; optionally carries a machine-readable error code. */
export class LlmApiError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'LlmApiError';
  }
}

/** OAuth token set for storing a provider credential. */
export interface OAuthCredentialDTO {
  access_token: string;
  refresh_token?: string;
  expires_at?: string;
}

/** Request body for creating or updating an LLM provider connection. */
export interface LlmConnectionUpsertRequestDTO {
  provider: string;
  auth_method: 'api_key' | 'oauth';
  api_key?: string;
  oauth?: OAuthCredentialDTO;
  label?: string;
}

/** A persisted LLM provider connection record. */
export interface LlmConnectionDTO {
  connection_id: string;
  provider: string;
  auth_method: string;
  status: string;
  token_expires_at?: string | null;
  last_used_at?: string | null;
  label?: string | null;
  created_at: string;
  updated_at: string;
}

/** Response from GET /users/me/llm-connections. */
export interface LlmConnectionListDTO {
  connections: LlmConnectionDTO[];
}

/** Result of a connection test including the resolved runtime model. */
export interface LlmConnectionTestDTO {
  connection_id: string;
  status: string;
  runtime_backend?: string | null;
  runtime_model?: string | null;
}

/** Optional overrides when testing a connection against a specific agent. */
export interface LlmConnectionTestRequestDTO {
  agent_id?: string | null;
}

/** Setup instructions for one LLM provider as returned by the help endpoint. */
export interface ProviderHelpItemDTO {
  provider: string;
  auth_methods: string[];
  setup_instructions: string[];
}

/** All provider help entries returned by GET /llm/providers/key-setup-help. */
export interface ProviderHelpResponseDTO {
  providers: ProviderHelpItemDTO[];
}

/** Union of all supported LLM provider identifiers used as keys throughout the app. */
export type LlmProviderKey =
  'claude' | 'codex' | 'copilot' | 'cursor' | 'aws_bedrock' | 'aws_bedrock_platform';

/** Status of a single LLM provider entry shown in the footer picker. */
export interface LlmFooterProviderDTO {
  provider: LlmProviderKey;
  connected: boolean;
  status: 'active' | 'expired' | 'reauth_required' | 'unavailable';
  requires_agent: boolean;
  connection_id?: string | null;
}

/** A selectable agent definition belonging to a specific LLM provider. */
export interface LlmProviderAgentDTO {
  agent_id: string;
  label: string;
}

/** Full state for the LLM provider footer picker, including the active selection and all available providers. */
export interface LlmFooterStateDTO {
  active_provider: LlmProviderKey | null;
  active_connection_id: string | null;
  active_agent_id: string | null;
  providers: LlmFooterProviderDTO[];
  agents: Partial<Record<LlmProviderKey, LlmProviderAgentDTO[]>>;
}

/** Request body for saving the user's active LLM provider selection. */
export interface LlmFooterSelectionRequestDTO {
  provider: LlmProviderKey;
  connection_id?: string | null;
  agent_id?: string | null;
}

/** Confirmation of the saved LLM footer selection including resolved status. */
export interface LlmFooterSelectionResponseDTO {
  provider: LlmProviderKey;
  connection_id: string | null;
  agent_id: string | null;
  status: 'active' | 'expired' | 'reauth_required' | 'unavailable';
}

/** List of agents available for a given LLM provider. */
export interface LlmProviderAgentsResponseDTO {
  provider: LlmProviderKey;
  agents: LlmProviderAgentDTO[];
}

/** A single AWS Bedrock cross-region inference profile option. */
export interface BedrockInferenceProfileOptionDTO {
  inference_profile_id: string;
  inference_profile_arn?: string | null;
  label: string;
}

/** All available Bedrock inference profiles for a region, including which are currently active. */
export interface BedrockInferenceProfilesResponseDTO {
  provider: 'aws_bedrock';
  region: string;
  active_profile_ids: string[];
  profiles: BedrockInferenceProfileOptionDTO[];
}

/** A single turn in a multi-turn chat history. */
export interface ChatHistoryTurnDTO {
  role: 'user' | 'assistant';
  content: string;
}

/** Controls the verbosity of knowledge retrieval summaries included in chat context. */
export type SummaryDetail = 'concise' | 'detailed';

/** Request body for sending a chat message to the project's active LLM provider. */
export interface ChatMessageRequestDTO {
  message: string;
  provider_override?: LlmProviderKey | null;
  agent_override?: string | null;
  knowledge_enabled?: boolean;
  history?: ChatHistoryTurnDTO[];
  summary_detail?: SummaryDetail;
}

/** A knowledge card cited in an LLM response. */
export interface CitationDTO {
  card_id: string;
  title: string;
  source_type: string;
  source_url: string | null;
}

/** The LLM's reply including cited knowledge cards and retrieval metadata. */
export interface ChatMessageResponseDTO {
  provider: LlmProviderKey;
  connection_id: string;
  agent_id: string | null;
  status: string;
  reply: string;
  citations: CitationDTO[];
  context_pack_id: string | null;
  retrieval_ms: number | null;
}

/** Request body for the async chat job enqueue endpoint. */
export interface ChatMessageJobRequestDTO {
  client_operation_id: string;
  payload: ChatMessageRequestDTO;
}

/** Request body for triggering a generic LLM task execution job. */
export interface LlmExecuteRequestDTO {
  provider?: string | null;
  agent_id?: string | null;
  task_type: string;
  input?: Record<string, unknown>;
}

/** Request body for the async LLM execute job enqueue endpoint. */
export interface LlmExecuteJobRequestDTO {
  client_operation_id: string;
  payload: LlmExecuteRequestDTO;
}

const llmApiClient = createApiClient({
  baseURL: config.apiUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Normalises any caught value into an LlmApiError, extracting backend `detail` and `code` fields
 * where available.
 */
const extractApiError = (error: unknown): LlmApiError => {
  handleSessionExpired(error);

  if (!axios.isAxiosError(error)) {
    const message = error instanceof Error ? error.message : 'Request failed';
    return new LlmApiError(message);
  }

  const data = error.response?.data as Record<string, unknown> | undefined;
  const topDetail = data?.detail;
  const topCode = data?.code;

  if (typeof topDetail === 'string') {
    return new LlmApiError(topDetail, typeof topCode === 'string' ? topCode : undefined);
  }

  if (typeof topDetail === 'object' && topDetail !== null) {
    const nestedDetail = (topDetail as Record<string, unknown>).detail;
    const nestedCode = (topDetail as Record<string, unknown>).code;
    if (typeof nestedDetail === 'string') {
      return new LlmApiError(nestedDetail, typeof nestedCode === 'string' ? nestedCode : undefined);
    }
  }

  return new LlmApiError(error.message);
};

/**
 * Lists all LLM provider connections for the current user.
 * GET /users/me/llm-connections
 */
export const listLlmConnections = async (accessToken: string): Promise<LlmConnectionListDTO> => {
  try {
    const response = await llmApiClient.get<LlmConnectionListDTO>('/users/me/llm-connections', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to list LLM connections', parsed);
    throw parsed;
  }
};

/**
 * Creates a new LLM provider connection for the current user.
 * POST /users/me/llm-connections
 */
export const createLlmConnection = async (
  accessToken: string,
  payload: LlmConnectionUpsertRequestDTO,
): Promise<LlmConnectionDTO> => {
  try {
    const response = await llmApiClient.post<LlmConnectionDTO>(
      '/users/me/llm-connections',
      payload,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to create LLM connection', parsed);
    throw parsed;
  }
};

/**
 * Sends a test request through the specified LLM connection to verify it is functional.
 * POST /users/me/llm-connections/{connectionId}/test
 *
 * @returns Test result including the resolved runtime backend and model.
 */
export const testLlmConnection = async (
  accessToken: string,
  connectionId: string,
  payload?: LlmConnectionTestRequestDTO,
): Promise<LlmConnectionTestDTO> => {
  try {
    const response = await llmApiClient.post<LlmConnectionTestDTO>(
      `/users/me/llm-connections/${connectionId}/test`,
      payload ?? {},
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to test LLM connection', parsed);
    throw parsed;
  }
};

/**
 * Permanently deletes an LLM provider connection.
 * DELETE /users/me/llm-connections/{connectionId}
 */
export const deleteLlmConnection = async (
  accessToken: string,
  connectionId: string,
): Promise<void> => {
  try {
    await llmApiClient.delete(`/users/me/llm-connections/${connectionId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to delete LLM connection', parsed);
    throw parsed;
  }
};

/**
 * Fetches setup instructions for all supported LLM providers (public, no auth required).
 * GET /llm/providers/key-setup-help
 */
export const getProviderHelp = async (): Promise<ProviderHelpResponseDTO> => {
  try {
    const response = await llmApiClient.get<ProviderHelpResponseDTO>(
      '/llm/providers/key-setup-help',
    );
    return response.data;
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to load provider help', parsed);
    throw parsed;
  }
};

/**
 * Loads the LLM provider footer state for a project, including all available providers and the active selection.
 * GET /clients/{clientId}/projects/{projectId}/llm/footer-state
 */
export const getLlmFooterState = async (
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<LlmFooterStateDTO> => {
  try {
    const response = await llmApiClient.get<LlmFooterStateDTO>(
      `/clients/${clientId}/projects/${projectId}/llm/footer-state`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to load LLM footer state', parsed);
    throw parsed;
  }
};

/**
 * Persists the user's active LLM provider selection for a project.
 * PUT /clients/{clientId}/projects/{projectId}/llm/footer-selection
 */
export const saveLlmFooterSelection = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  payload: LlmFooterSelectionRequestDTO,
): Promise<LlmFooterSelectionResponseDTO> => {
  try {
    const response = await llmApiClient.put<LlmFooterSelectionResponseDTO>(
      `/clients/${clientId}/projects/${projectId}/llm/footer-selection`,
      payload,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to save LLM footer selection', parsed);
    throw parsed;
  }
};

/**
 * Lists available agents for a given LLM provider, optionally scoped to a connection.
 * GET /users/me/llm-providers/{provider}/agents
 */
export const listLlmProviderAgents = async (
  accessToken: string,
  provider: LlmProviderKey,
  connectionId?: string | null,
): Promise<LlmProviderAgentsResponseDTO> => {
  try {
    const response = await llmApiClient.get<LlmProviderAgentsResponseDTO>(
      `/users/me/llm-providers/${provider}/agents`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: connectionId ? { connection_id: connectionId } : undefined,
      },
    );
    return response.data;
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to list provider agents', parsed);
    throw parsed;
  }
};

/**
 * Fetches the stored list of AWS Bedrock cross-region inference profiles for the user's connection.
 * GET /users/me/llm-providers/aws_bedrock/inference-profiles
 */
export const listBedrockInferenceProfiles = async (
  accessToken: string,
  options?: { connectionId?: string | null; region?: string | null },
): Promise<BedrockInferenceProfilesResponseDTO> => {
  try {
    const response = await llmApiClient.get<BedrockInferenceProfilesResponseDTO>(
      '/users/me/llm-providers/aws_bedrock/inference-profiles',
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: {
          ...(options?.connectionId ? { connection_id: options.connectionId } : {}),
          ...(options?.region ? { region: options.region } : {}),
        },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to list Bedrock inference profiles', parsed);
    throw parsed;
  }
};

/**
 * Dynamically discovers Bedrock inference profiles using a runtime AWS token.
 * POST /users/me/llm-providers/aws_bedrock/inference-profiles/discover
 *
 * @param payload.runtimeToken - Temporary AWS credentials used for the Bedrock ListFoundationModels call.
 */
export const discoverBedrockInferenceProfiles = async (
  accessToken: string,
  payload: { runtimeToken: string; region?: string | null },
): Promise<BedrockInferenceProfilesResponseDTO> => {
  try {
    const response = await llmApiClient.post<BedrockInferenceProfilesResponseDTO>(
      '/users/me/llm-providers/aws_bedrock/inference-profiles/discover',
      {
        runtime_token: payload.runtimeToken,
        ...(payload.region ? { region: payload.region } : {}),
      },
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to discover Bedrock inference profiles', parsed);
    throw parsed;
  }
};

/**
 * Saves the selected Bedrock inference profile IDs to the user's connection record.
 * PUT /users/me/llm-connections/{connectionId}/bedrock-inference-profiles
 *
 * @param inferenceProfileIds - IDs of the profiles to enable for this connection.
 * @returns The updated LLM connection record.
 */
export const updateBedrockInferenceProfiles = async (
  accessToken: string,
  connectionId: string,
  inferenceProfileIds: string[],
): Promise<LlmConnectionDTO> => {
  try {
    const response = await llmApiClient.put<LlmConnectionDTO>(
      `/users/me/llm-connections/${connectionId}/bedrock-inference-profiles`,
      {
        inference_profile_ids: inferenceProfileIds,
      },
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to update Bedrock inference profiles', parsed);
    throw parsed;
  }
};

/**
 * Sends a chat message to the project's active LLM provider (synchronous, 2 min timeout).
 * POST /clients/{clientId}/projects/{projectId}/chat/messages
 *
 * @returns The LLM reply with citations and retrieval metadata.
 */
export const sendChatMessage = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  payload: ChatMessageRequestDTO,
): Promise<ChatMessageResponseDTO> => {
  try {
    const response = await llmApiClient.post<ChatMessageResponseDTO>(
      `/clients/${clientId}/projects/${projectId}/chat/messages`,
      payload,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 120_000,
      },
    );
    return response.data;
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to send chat message', parsed);
    throw parsed;
  }
};

/**
 * Enqueues a chat message as an async job, returning the SSE job handle.
 * POST /clients/{clientId}/projects/{projectId}/chat/jobs
 *
 * @param clientOperationId - Idempotency key; auto-generated when omitted.
 */
export const startChatMessageJob = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  payload: ChatMessageRequestDTO,
  clientOperationId = createClientOperationId('chat'),
): Promise<EventJobStartResponseDTO> => {
  try {
    const response = await llmApiClient.post<EventJobStartResponseDTO>(
      `/clients/${clientId}/projects/${projectId}/chat/jobs`,
      { client_operation_id: clientOperationId, payload } satisfies ChatMessageJobRequestDTO,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 15_000,
      },
    );
    return response.data;
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to start chat message job', parsed);
    throw parsed;
  }
};

/**
 * End-to-end helper: enqueues a chat message job and streams SSE events until the reply arrives.
 *
 * @returns The completed chat response from the job's final snapshot.
 */
export const sendChatMessageViaEvents = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  payload: ChatMessageRequestDTO,
): Promise<ChatMessageResponseDTO> => {
  const started = await startChatMessageJob(accessToken, clientId, projectId, payload);
  const snapshot = await waitForEventJob<ChatMessageResponseDTO>(accessToken, started);
  return snapshot.result;
};

/**
 * Enqueues a generic LLM task execution job for a project.
 * POST /clients/{clientId}/projects/{projectId}/llm/execute-jobs
 *
 * @param clientOperationId - Idempotency key; auto-generated when omitted.
 * @returns Job start metadata for SSE streaming.
 */
export const startLlmExecuteJob = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  payload: LlmExecuteRequestDTO,
  clientOperationId = createClientOperationId('llm-execute'),
): Promise<EventJobStartResponseDTO> => {
  try {
    const response = await llmApiClient.post<EventJobStartResponseDTO>(
      `/clients/${clientId}/projects/${projectId}/llm/execute-jobs`,
      { client_operation_id: clientOperationId, payload } satisfies LlmExecuteJobRequestDTO,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 15_000,
      },
    );
    return response.data;
  } catch (error) {
    const parsed = extractApiError(error);
    logger.error('Failed to start LLM execute job', parsed);
    throw parsed;
  }
};
